import React, {useEffect, useMemo, useState} from 'react';
import {v4} from 'uuid';
import {t} from 'i18next';
import {Box, Typography, IconButton} from '@mui/material';
import {GridCellParams, GridColDef, GridSortModel} from '@mui/x-data-grid';
import './IngetionUnpublishDocument.scss';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {
  useIngestionExportUnpublishDocData,
  useUnpublishHistoryData,
  useUnpublishIngestion,
  useUnpublishSearchTreeData,
} from '../../hooks/ingestions/useIngestions';
import {
  DocumentSearchTreeListType,
  ToastMessageConfigType,
  UnpublishHistoryMapkeys,
  UnpublishHistoryMappedRecordType,
} from '../../api/types/ingestionApi.type';
import ExpandableTreeView from '../doc-explorer/doc-expandable-tree/ExpandableTreeView';
import {PAGE_SIZE, ToastType} from '../../shared/constants';
import ArrDataGrid from '../common/arr-data-grid/ArrDataGrid';
import ArrButton from '../common/arr-button/ArrButton';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';

interface TreeNode extends DocumentSearchTreeListType {
  children?: TreeNode[];
  label: string;
  isChildrenLoaded?: boolean;
  hasChildren?: boolean;
}

const defaultSortColumn = {
  field: 'DateUnpublished',
  sort: 'asc',
};

const stringPath = 'ingestions.unpublishDocument.';

const IngestionUnpulishDocuments = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(PAGE_SIZE);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [selectedSavedSearchId, setSelectedSavedSearchId] = useState<
    string | null
  >(null);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const {
    data: searchTreeData,
    // isError: isSearchTreeDataError,
    // error: searchTreeDataError,
  } = useUnpublishSearchTreeData();

  const {
    data: historyData,
    isLoading: isHistoryDataLoading,
    refetch: refreshHistoryDataList,
    isError: isHistoryDataError,
    error: historyDataError,
  } = useUnpublishHistoryData({
    pageNumber: pageNumber,
    pageSize: itemPerPage,
    sortingColumn: sortModel[0]?.field ?? defaultSortColumn.field,
    actionCode: '',
    isAscending: sortModel[0]?.sort ?? defaultSortColumn.sort,
  });

  const {mutate: unpublishIngestion, isPending: isUnpublishPending} =
    useUnpublishIngestion();

  const {mutate: exportUnpublishDocIdReq} =
    useIngestionExportUnpublishDocData();

  // Effect to handle errors in history data
  useEffect(() => {
    if (isHistoryDataError && historyDataError) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          historyDataError?.message ??
          t(stringPath + 'historyDataErrorGenericMsg'),
      });
    }
  }, [isHistoryDataError, historyDataError, t]);

  const buildedTreeData = useMemo(() => {
    const map = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    // Initialize map with all items
    for (const item of searchTreeData?.searchTreeDetails ?? []) {
      map.set(item.id, {...item, label: item.text, children: []});
    }

    for (const node of map.values()) {
      if (node.parent === '#' || !node.parent) {
        roots.push({...node, isChildrenLoaded: true, hasChildren: true});
      } else {
        const parentNode = map.get(node.parent);
        if (parentNode) {
          parentNode.children!.push({
            ...node,
            isChildrenLoaded: true,
            hasChildren: true,
          });
        } else {
          // If parent missing, treat as root
          roots.push({...node, isChildrenLoaded: true, hasChildren: true});
        }
      }
    }

    return roots;
  }, [searchTreeData?.searchTreeDetails]);

  const historyGridColumn = useMemo<GridColDef[]>(() => {
    const namePath = stringPath + 'grid.';
    return [
      {
        field: 'SearchName',
        headerName: t(namePath + 'search'),
        headerClassName: 'search-name-column',
        flex: 1,
        cellClassName: 'search-name-cell',
      },
      {
        field: 'DocumentsCount',
        headerName: t(namePath + 'docCount'),
        flex: 1,
        headerClassName: 'document-count-header',
        cellClassName: 'document-count-cell',
      },
      {field: 'Status', headerName: t(namePath + 'status'), flex: 1},
      {
        field: 'UserUnPublished',
        headerName: t(namePath + 'userUnpublished'),
        flex: 1,
      },
      {
        field: 'DateUnpublished',
        headerName: t(namePath + 'dateUnpublsihed'),
        flex: 1,
        headerClassName: 'last-column',
      },
    ];
  }, [t]);

  const historyGridData = useMemo<UnpublishHistoryMappedRecordType[]>(() => {
    const result: UnpublishHistoryMappedRecordType[] = (
      historyData?.data ?? []
    ).map((row) => {
      const obj: {[key: string]: string | number} = {};
      for (const [index, value] of UnpublishHistoryMapkeys.entries()) {
        if (value === 'id') {
          obj[value] = v4();
          obj['SearchId'] = row[index];
        } else {
          obj[value] = row[index];
        }
      }
      return obj as unknown as UnpublishHistoryMappedRecordType;
    });
    return result;
  }, [historyData?.data]);

  const dataGridClass = useMemo(() => {
    if (isHistoryDataLoading) return 'history-grid-loading';

    if ((historyData?.totalRecords ?? 0) === 0)
      return 'history-grid-no-row-overlay';

    return 'history-grid';
  }, [isHistoryDataLoading, historyData]);

  const handleUnpublishClick = () => {
    // Implement the unpublish click handler logic here
    unpublishIngestion(
      {analyzingSearchId: selectedSavedSearchId ?? ''},
      {
        onSuccess: (data) => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Success,
            message: data.message,
          });
          setSelectedSavedSearchId(null);
          // Refresh history data list after unpublish
          setPageNumber(1);
          setTimeout(() => {
            refreshHistoryDataList();
          }, 200);
        },
        onError: (error) => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Error,
            message:
              error?.message ?? t(stringPath + 'unpublishErrorGenericMsg'),
          });
        },
      }
    );
  };

  const handleUnpublishDocCelllClick = (params: GridCellParams) => {
    const {field, row}: {field: string; row: UnpublishHistoryMappedRecordType} =
      params;
    if (field === 'DocumentsCount') {
      exportUnpublishDocIdReq(
        {analyzingSearchId: row.SearchId},
        {
          onSuccess: (data) => {
            setToastMsgConfig({
              show: true,
              type: ToastType.Success,
              message: data.message,
            });
          },
          onError: () => {
            setToastMsgConfig({
              show: true,
              type: ToastType.Error,
              message: t(stringPath + 'exportDocErrorGenericMsg'),
            });
          },
        }
      );
    }
  };

  const renderHeader = () => {
    return (
      <Box className="unpublish-doc-header">
        <Box className="title-container">
          <Typography
            className="typo-text headline3_size title-label"
            tabIndex={0}
            data-testid={`ingetion-unpublish-documents-title`}
          >
            {t(stringPath + 'title')}
          </Typography>
          <ArrTooltipWrapper
            trigger={
              <IconButton
                size="small"
                className="info-icon"
                tabIndex={0}
                aria-label={t('ingestions.unpublishDocument.moreInfo')}
              >
                <InfoIcon fontSize="medium" className="info-icon" />
              </IconButton>
            }
            title={t(stringPath + 'title')}
            placement="top"
            data-testid="title-info-tooltip"
          >
            {t(stringPath + 'infoTooltipMsg')
              .split('\n')
              .map((line) => (
                <React.Fragment key={v4()}>
                  {line}
                  <br data-testid={`ingetion-unpublish-documents-br`} />
                </React.Fragment>
              ))}
          </ArrTooltipWrapper>
        </Box>
        <Typography
          className="typo-text body2_size description-label"
          data-testid={`ingetion-unpublish-documents-description`}
        >
          {t(stringPath + 'description')}
        </Typography>
      </Box>
    );
  };

  const renderLeftTreeView = () => {
    return (
      <Box className="saved-search-left-panel">
        <ExpandableTreeView
          itemsList={buildedTreeData}
          expandedNodes={['-1']}
          multipleSelection={false}
          setSelectedItem={(item, _isCtrlPressed, node) => {
            if (!node?.isGroup && node?.children?.length === 0) {
              setSelectedSavedSearchId(item ? item[0] : null);
            }
          }}
          headerComponent={
            <Box className="search-search-header">
              <Typography
                className="typo-text subhead3_size title-label"
                tabIndex={0}
                data-testid={`ingetion-unpublish-documents-saved-search`}
              >
                {t(stringPath + 'savedSearch')}
              </Typography>
            </Box>
          }
          footerComponent={
            <Box className="footer-button-container">
              <ArrButton
                variant="primary"
                size="medium"
                onClick={handleUnpublishClick}
                dataTestId="unpublish-button"
                disabled={isUnpublishPending || !selectedSavedSearchId}
              >
                {t(stringPath + (isUnpublishPending ? 'analyze' : 'unpublish'))}
              </ArrButton>
            </Box>
          }
        />
      </Box>
    );
  };

  const renderRightHistoryGridView = () => {
    return (
      <Box className="history-grid-wrapper">
        <Box className="history-title-container">
          <Typography
            className="typo-text subhead3_size title-label"
            tabIndex={0}
            data-testid={`ingetion-unpublish-documents-unpublished-history`}
          >
            {t(stringPath + 'unpublishHistory')}
          </Typography>
          <ArrTooltipWrapper
            trigger={
              <IconButton
                size="small"
                className="info-icon"
                aria-label={t('ingestions.unpublishDocument.moreInfo')}
              >
                <InfoIcon fontSize="medium" className="info-icon" />
              </IconButton>
            }
            title={t(stringPath + 'title')}
            placement="top"
            data-testid="unpublish-history-info-tooltip"
          >
            {t(stringPath + 'unpublishHistoryInfoTooltip')}
          </ArrTooltipWrapper>
        </Box>
        {/** History Grid */}
        <Box className="unpublis-history-grid-container">
          <ArrDataGrid
            rows={historyGridData}
            rowCount={historyData?.totalRecords ?? 0}
            columns={historyGridColumn}
            paginationMode="server"
            primaryKey="id"
            loading={isHistoryDataLoading}
            showPerPageOptions
            pageSizeOptions={[10]}
            itemsPerPage={itemPerPage}
            onPageChange={setPageNumber}
            onSortModelChange={setSortModel}
            currentPage={pageNumber}
            setItemsPerPage={setItemPerPage}
            hideFooter={(historyData?.totalRecords ?? 0) === 0}
            alwaysShowPagination
            entityType={t('ingestions.common.entries')}
            className={dataGridClass}
            onCellClick={handleUnpublishDocCelllClick}
            noRowsOverlayCustomText={t(stringPath + 'noRowsText')}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box className="ingetion-unpublish-document-container">
      {renderHeader()}
      <Box className="tree-grid-container">
        {renderLeftTreeView()}
        {renderRightHistoryGridView()}
      </Box>
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
    </Box>
  );
};

export default IngestionUnpulishDocuments;
